/**
 * Schémas Zod — Agent Identities (D30 — Chunk 9.M.5).
 *
 * Source DB : `app.agent_identities` (migration 0038, ADR 0021 §2.4).
 *
 * Matérialise les identités sémantiques derrière les `app.api_keys` d'agents
 * IA / systèmes externes. Sans cette table, les colonnes `*_by_agent`
 * existantes (déclarées NULL non contraintes par migration 0021) ne peuvent
 * pas être joinées vers un nom humain lisible côté forensic.
 *
 * Décisions PO 9.M.5 :
 *   - Q3 = Aucun backfill automatique des `*_by_agent` existants — toutes
 *          les rows historiques restent NULL ; seules les nouvelles writes
 *          faites via api_key reliée à une agent_identity peuplent les
 *          colonnes.
 *   - Q4 = `kind` est un enum LOCAL à la table (pas une référence à
 *          `app.user_role` qui sera étendu plus tard en D28).
 *
 * Scope auth Phase 1 :
 *   - GET list/detail : tout `authenticated` (RLS filtre tenant scope + global).
 *   - POST/PATCH : super_admin only Phase 1 (RLS `is_super_admin()`).
 *   - DELETE : refusé RLS (soft-disable via UPDATE `disabled_at`).
 */

import { z } from "zod";

import { PaginationSchema, QueryBooleanSchema, UuidSchema } from "./common.js";

// -----------------------------------------------------------------------------
// Enum AgentIdentityKind (miroir DB CHECK constraint 0038)
// -----------------------------------------------------------------------------
//
// Enum LOCAL à la table `agent_identities` — pas une référence à
// `app.user_role`. Si D28 (enum extension user_role) ship plus tard avec
// `ai_operator`/`external_system`, les 2 enums coexisteront sans dépendance.

export const AgentIdentityKindSchema = z.enum(["ai_operator", "external_system"]);
export type AgentIdentityKind = z.infer<typeof AgentIdentityKindSchema>;

// -----------------------------------------------------------------------------
// Row (lecture) — GET /v1/agent-identities[/:id]
// -----------------------------------------------------------------------------

export const AgentIdentityRowSchema = z.object({
  id: UuidSchema,
  // NULL = agent global Kollectivia (visible par tous les tenants).
  tenant_id: UuidSchema.nullable(),
  kind: AgentIdentityKindSchema,
  display_name: z.string(),
  description: z.string().nullable(),
  avatar_url: z.string().nullable(),

  created_by: UuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  disabled_at: z.string().nullable(),
});
export type AgentIdentityRow = z.infer<typeof AgentIdentityRowSchema>;

// -----------------------------------------------------------------------------
// Filtres listing — GET /v1/agent-identities
// -----------------------------------------------------------------------------
//
// Filtres optionnels (cardinalité faible Phase 1, pas de refine requis) :
//   - kind                  → filter sur ai_operator ou external_system
//   - tenant_id             → filter sur un tenant précis (super_admin only en pratique)
//   - include_global        → bool (par défaut true) — inclure les agents globaux
//                              (NULL tenant_id) dans la liste
//   - include_disabled      → bool (par défaut false) — inclure les agents
//                              soft-disabled (`disabled_at` non-null)

export const AgentIdentityFilterSchema = z.object({
  kind: AgentIdentityKindSchema.optional(),
  tenant_id: UuidSchema.optional(),
  include_global: QueryBooleanSchema.optional(),
  include_disabled: QueryBooleanSchema.optional(),
});
export type AgentIdentityFilter = z.infer<typeof AgentIdentityFilterSchema>;

export const AgentIdentityListQuerySchema = PaginationSchema.merge(AgentIdentityFilterSchema);
export type AgentIdentityListQuery = z.infer<typeof AgentIdentityListQuerySchema>;

// -----------------------------------------------------------------------------
// Create (POST /v1/agent-identities) — super_admin only Phase 1
// -----------------------------------------------------------------------------
//
// `tenant_id` :
//   - NULL → agent global Kollectivia (visible par tous les tenants).
//   - non-NULL → agent appartenant à un tenant spécifique.
//   Le handler valide que `tenant_id` (si fourni) existe.
//
// `kind` strict via enum ; rejet 400 `AGENT_IDENTITIES_KIND_INVALID` si valeur
// hors enum (le CHECK DB rebondit sinon en exception PG générique non-déterministe).

export const CreateAgentIdentitySchema = z.object({
  tenant_id: UuidSchema.nullable().optional(),
  kind: AgentIdentityKindSchema,
  display_name: z.string().min(1).max(200),
  description: z.string().min(1).max(2000).optional(),
  avatar_url: z.string().min(1).max(500).optional(),
});
export type CreateAgentIdentityInput = z.infer<typeof CreateAgentIdentitySchema>;

// -----------------------------------------------------------------------------
// Update (PATCH /v1/agent-identities/:id) — super_admin only Phase 1
// -----------------------------------------------------------------------------
//
// Champs modifiables Phase 1 :
//   - display_name (1..200)
//   - description (1..2000) | null
//   - avatar_url (1..500) | null
//   - disabled (bool) → set/unset `disabled_at` (soft-disable réversible)
//
// NON modifiables (immutable Phase 1) :
//   - tenant_id (changer le scope d'un agent = risque forensic)
//   - kind (changer le type d'un agent = risque forensic)
//   - created_by, created_at, id

export const UpdateAgentIdentitySchema = z.object({
  display_name: z.string().min(1).max(200).optional(),
  description: z.string().min(1).max(2000).nullable().optional(),
  avatar_url: z.string().min(1).max(500).nullable().optional(),
  disabled: z.boolean().optional(),
});
export type UpdateAgentIdentityInput = z.infer<typeof UpdateAgentIdentitySchema>;
